/* ============================================================
   SLICK STORE — SHARED CART ENGINE
   File: assets/ss-cart.js
   ============================================================
   Single source of truth for ALL cart behavior:
     - Fetch /cart.js and build a cart map (variantId -> qty)
     - Check if a variant is "maxed" in cart vs inventory
     - Add to cart via /cart/add.js
     - Render Dawn cart drawer / cart notification
     - Update cart count bubble in header
     - Fire cart:updated / cart:refresh events
     - Cross-tab sync via storage event (ss-cart-ts key)
 
   Loaded from layout/theme.liquid so EVERY page has it.
 
   Replaces inline duplicates in:
     - snippets/card-product.liquid  (window.ssGridAddToCart)
     - sections/related-products.liquid  (relatedAddToCart)
     - snippets/ss-product-info.liquid  (dawnAtcAdd partial)
 
   The product page keeps its own ATC wrapper that calls
   SlickCart.add() under the hood, because it has extra
   responsibilities: gift wrap, Buy Now, sticky bar, variant
   change handling, quantity input.
   ============================================================ */
 
(function () {
  'use strict';
 
  /* ------------------------------------------------------------
     Internal state
     ------------------------------------------------------------
     cartQtyMap: { "variantId": quantityInCart }
     Updated after every /cart.js fetch.
  ------------------------------------------------------------ */
  var cartQtyMap = {};
  var ready = false;
  var STORAGE_TS_KEY = 'ss-cart-ts';
 
  /* ------------------------------------------------------------
     getDawnCartElement()
     ------------------------------------------------------------
     Dawn theme uses one of two custom elements for cart UI:
     <cart-drawer> if the theme is set to "drawer" mode,
     <cart-notification> if set to "notification" mode.
     We support both.
  ------------------------------------------------------------ */
  function getDawnCartElement() {
    return document.querySelector('cart-drawer') ||
           document.querySelector('cart-notification');
  }
 
  /* ------------------------------------------------------------
     getDawnSectionsToRender(cartEl)
     ------------------------------------------------------------
     Dawn's Section Rendering API needs us to tell it which
     sections to re-render after a cart change. The cart-drawer
     and cart-notification elements know their own answer to
     this. If neither is present, fall back to the bare minimum.
  ------------------------------------------------------------ */
  function getDawnSectionsToRender(cartEl) {
    if (!cartEl) return [];
    try {
      if (typeof cartEl.getSectionsToRender === 'function') {
        return cartEl.getSectionsToRender().map(function (s) { return s.id; });
      }
    } catch (e) {}
    return ['cart-icon-bubble', 'cart-live-region-text'];
  }
 
  /* ------------------------------------------------------------
     rebuildCartMap(cart)
     ------------------------------------------------------------
     Given a cart object from /cart.js, rebuild the cart map.
     Handles items with same variant_id appearing in multiple
     lines (rare but possible) by summing quantities.
  ------------------------------------------------------------ */
  function rebuildCartMap(cart) {
    cartQtyMap = {};
    if (cart && Array.isArray(cart.items)) {
      cart.items.forEach(function (item) {
        var key = String(item.variant_id);
        cartQtyMap[key] = (cartQtyMap[key] || 0) + item.quantity;
      });
    }
    ready = true;
  }
 
  /* ------------------------------------------------------------
     getCartQty(variantId)
     ------------------------------------------------------------
     How many of this variant are currently in cart?
  ------------------------------------------------------------ */
  function getCartQty(variantId) {
    return cartQtyMap[String(variantId)] || 0;
  }
 
  /* ------------------------------------------------------------
     isMaxed(btn)
     ------------------------------------------------------------
     Reads inventory-related data attributes off the Add button
     and decides if "Max in cart" should apply.
 
     Logic priority (matches the rules in original card-product):
       1. If not available -> NOT max, it's sold-out
       2. If untracked inventory -> never max
       3. If continue-selling policy -> never max
       4. If tracked + deny policy + inCart >= inventory -> MAX
 
     Expected data attrs on the button:
       data-variant-id, data-inventory-quantity,
       data-inventory-management, data-inventory-policy,
       data-available
  ------------------------------------------------------------ */
  function isMaxed(btn) {
    if (!btn) return false;
    var variantId  = btn.getAttribute('data-variant-id');
    var qty        = parseInt(btn.getAttribute('data-inventory-quantity') || '0', 10);
    var management = btn.getAttribute('data-inventory-management');
    var policy     = btn.getAttribute('data-inventory-policy');
    var available  = btn.getAttribute('data-available') !== 'false';
    var inCart     = getCartQty(variantId);
 
    if (!available)             return false;
    if (!management)            return false;
    if (policy === 'continue')  return false;
 
    return management === 'shopify' && qty > 0 && inCart >= qty;
  }
 
  /* ------------------------------------------------------------
     hardMax(inventory, variantId)
     ------------------------------------------------------------
     Returns the maximum quantity that can be added to cart
     for this variant. Used by the product page quantity input.
     Subtracts what's already in cart from total inventory.
 
     inventory shape: { qty, management, policy, available }
  ------------------------------------------------------------ */
  function hardMax(inventory, variantId) {
    if (!inventory) return 0;
    if (inventory.policy === 'continue') return 9999;
    if (inventory.management === 'shopify' && inventory.qty > 0) {
      var inCart = getCartQty(variantId);
      return Math.max(0, inventory.qty - inCart);
    }
    return 9999;
  }
 
  /* ------------------------------------------------------------
     syncGridButtons()
     ------------------------------------------------------------
     Loop over every .ss-card-add button on the page and set
     its visual state based on the current cart map:
       - "Sold Out" if unavailable
       - "Max" if maxed in cart
       - "Add" otherwise
 
     Skips buttons currently mid-add (have .is-loading class).
  ------------------------------------------------------------ */
  function syncGridButtons() {
    document.querySelectorAll('.ss-card-add[data-variant-id]').forEach(function (btn) {
      if (btn.classList.contains('is-loading')) return;
 
      var label = btn.querySelector('span');
      var available = btn.getAttribute('data-available') !== 'false';
 
      if (!available || btn.classList.contains('ss-card-add--off')) {
        btn.disabled = true;
        if (label) label.textContent = 'Sold Out';
        return;
      }
 
      if (isMaxed(btn)) {
        btn.disabled = true;
        btn.classList.add('ss-card-add--max');
        if (label) label.textContent = 'Max';
      } else {
        btn.disabled = false;
        btn.classList.remove('ss-card-add--max');
        if (label) {
          var txt = label.textContent.trim();
          if (txt === 'Max' || txt === 'Sold Out' || txt === '✓ Added' || txt === 'Error') {
            label.textContent = 'Add';
          }
        }
      }
    });
    syncSteppers();
  }
 
  /* ------------------------------------------------------------
     updateCartBubble(cart)
     ------------------------------------------------------------
     Updates the header cart count bubble.
     If the bubble doesn't exist yet (empty cart on first load),
     create one.
  ------------------------------------------------------------ */
  /* ------------------------------------------------------------
     realItemCount(cart) — ROUND 4 (2026-07-18): the SINGLE shared
     "what number does the shopper see" helper. Neither the
     marketplace-fee line nor the gift-wrap line counts (round 9,
     founder: gift wrap is hidden from the item list, so counting it
     made the badge look wrong). Used by the header badge on every
     page and by the View-cart pill; the same rule is mirrored
     server-side in snippets/ss-cart-real-count.liquid and in the
     inline ssRealCount copies in slick-featured / shoppable-videos.
  ------------------------------------------------------------ */
  function realItemCount(cart) {
    if (!cart) return 0;
    var count = cart.item_count || 0;
    var cfg = window.SS_CART_CONFIG || {};
    if (Array.isArray(cart.items)) {
      cart.items.forEach(function (item) {
        /* round 9 (founder): gift wrap is hidden from the item list, so it no
           longer counts in the badge either — same rule as the fee. */
        if (item.handle === cfg.feeHandle || item.handle === 'gift-wrap') count -= item.quantity;
      });
    }
    return count < 0 ? 0 : count;
  }

  function updateCartBubble(cart) {
    if (!cart) return;
    var count = realItemCount(cart);
 
    document.querySelectorAll('.cart-count-bubble span[aria-hidden]').forEach(function (el) {
      el.textContent = count;
    });
 
    document.querySelectorAll('.cart-count-bubble').forEach(function (el) {
      el.classList.remove('hidden');
      /* fee-only or empty cart -> no badge at all (matches server render) */
      el.style.display = count > 0 ? '' : 'none';
    });
 
    var cartIcon = document.getElementById('cart-icon-bubble');
    if (cartIcon && !cartIcon.querySelector('.cart-count-bubble') && count > 0) {
      var bubble = document.createElement('div');
      bubble.className = 'cart-count-bubble';
      bubble.innerHTML =
        '<span aria-hidden="true">' + count + '</span>' +
        '<span class="visually-hidden">' + count + ' items</span>';
      cartIcon.appendChild(bubble);
    }
  }
 
  /* ------------------------------------------------------------
     fetchCart(callback)
     ------------------------------------------------------------
     Fetch /cart.js, rebuild the cart map, sync UI.
     Returns a Promise. Callback (if provided) is called with
     the cart object on success, or null on failure.
  ------------------------------------------------------------ */
  function fetchCart(callback, isRetry) {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) {
        /* ROUND 4: one retry on transient failure (e.g. an edge 429) —
           without it a failed boot fetch leaves the server-rendered badge
           uncorrected on CDN-cached pages (founder-seen 4-vs-3 flash). */
        if (!r.ok) throw new Error('cart.js HTTP ' + r.status);
        return r.json();
      })
      .then(function (cart) {
        rebuildCartMap(cart);
        updateCartBubble(cart);
        syncGridButtons();
        /* BLINKIT CART PAGE (2026-07-18): let the fee-enforcement module see
           every fresh cart state without changing any public API. */
        try {
          document.dispatchEvent(new CustomEvent('ss-cart:fetched', { detail: { cart: cart } }));
        } catch (e) {}
        if (callback) callback(cart);
        return cart;
      })
      .catch(function (err) {
        if (!isRetry) {
          return new Promise(function (resolve) {
            setTimeout(function () { resolve(fetchCart(callback, true)); }, 3000);
          });
        }
        ready = true;
        if (callback) callback(null);
        return null;
      });
  }
 
  /* ------------------------------------------------------------
     signalCrossTab()
     ------------------------------------------------------------
     Writes a timestamp to localStorage to notify other open
     tabs that the cart changed. Other tabs listen for 'storage'
     events on this key and re-fetch their own cart state.
  ------------------------------------------------------------ */
  function signalCrossTab() {
    try {
      localStorage.setItem(STORAGE_TS_KEY, String(Date.now()));
    } catch (e) {
      // localStorage unavailable, skip cross-tab sync
    }
  }
 
  /* ------------------------------------------------------------
     add(variantId, quantity, options)
     ------------------------------------------------------------
     The main entry point for ALL cart additions.
     options = {
       button: optional DOM element to manage loading state,
       onSuccess: callback(response) on success,
       onError: callback(error) on failure,
       openDrawer: boolean (default true) - whether to open
         the Dawn cart drawer/notification on success
     }
  ------------------------------------------------------------ */
  function add(variantId, quantity, options) {
    options = options || {};
    quantity = quantity || 1;
 
    var btn = options.button || null;
    var cartEl = getDawnCartElement();
    var sections = getDawnSectionsToRender(cartEl);
 
    var formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', quantity);
    if (sections.length && options.openDrawer !== false) {
      formData.append('sections', sections.join(','));
      formData.append('sections_url', window.location.pathname);
    }
 
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Accept': 'application/javascript',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: formData,
      credentials: 'same-origin'
    })
    .then(function (response) { return response.json(); })
    .then(function (response) {
      if (response.status && response.status !== 200) {
        throw response;
      }
 
      // Open Dawn cart UI if requested
      if (options.openDrawer !== false && cartEl && typeof cartEl.renderContents === 'function') {
        try {
          if (btn && typeof cartEl.setActiveElement === 'function') {
            cartEl.setActiveElement(btn);
          }
          cartEl.renderContents(response);
        } catch (e) {
          console.warn('Cart drawer render failed (item was still added):', e);
        }
      }
 
      // Refresh cart state and notify everyone
      return fetchCart().then(function (cart) {
        document.dispatchEvent(new CustomEvent('cart:updated', {
          detail: { source: 'ss-cart-add', cart: cart, variantId: variantId }
        }));
        document.dispatchEvent(new CustomEvent('cart:refresh', {
          detail: { cart: cart }
        }));
        signalCrossTab();
 
        if (options.onSuccess) options.onSuccess(response);
        return response;
      });
    })
    .catch(function (err) {
      console.error('SlickCart.add failed:', err);
      if (options.onError) options.onError(err);
      // Even on failure, refresh state so UI doesn't show stale "Adding..."
      fetchCart();
      throw err;
    });
  }
 
  /* ------------------------------------------------------------
     Public API
  ------------------------------------------------------------ */

  /* ------------------------------------------------------------
     window.ssGridAddToCart(btn, event) — ROUND 4 (2026-07-18)
     ------------------------------------------------------------
     The uniform card's ADD button handler (every surface: collection,
     search, PDP related, cart recommendations — the homepage featured
     card keeps its own spc-add handler wired to the same SlickCart).
     Grafted from the pre-blinkit live theme's card-product inline
     script; lives HERE because the cart page's recommendation cards
     arrive via innerHTML injection, where inline <script> never runs
     (only inline onclick attributes do). Visual states: '...' while
     adding, '✓ Added', then back to 'Add' / 'Max' / 'Sold Out' via
     SlickCart.syncGridButtons().
  ------------------------------------------------------------ */
  window.ssGridAddToCart = function (btn, event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') {
        event.stopImmediatePropagation();
      }
    }

    if (!btn || btn.classList.contains('is-loading')) return false;

    var variantId = btn.getAttribute('data-variant-id');
    var label = btn.querySelector('span');
    if (!variantId) {
      if (label) label.textContent = 'Error';
      return false;
    }

    /* Refresh cart state first so we never add past max-in-cart
       (e.g. another tab added items meanwhile). */
    fetchCart().then(function () {
      if (isMaxed(btn)) {
        btn.disabled = true;
        btn.classList.add('ss-card-add--max');
        if (label) label.textContent = 'Max';
        return;
      }

      btn.classList.add('is-loading');
      btn.setAttribute('aria-busy', 'true');
      btn.disabled = true;
      if (label) label.textContent = '...';

      add(variantId, 1, {
        button: btn,
        onSuccess: function () {
          if (label) label.textContent = '✓ Added';
          setTimeout(function () {
            btn.classList.remove('is-loading');
            btn.removeAttribute('aria-busy');
            syncGridButtons();
            if (!isMaxed(btn) && label && !btn.classList.contains('ss-card-add--off')) {
              label.textContent = 'Add';
              btn.disabled = false;
            } else {
              btn.disabled = true;
            }
          }, 900);
        },
        onError: function () {
          if (label) label.textContent = 'Error';
          setTimeout(function () {
            btn.classList.remove('is-loading');
            btn.removeAttribute('aria-busy');
            btn.disabled = false;
            syncGridButtons();
            if (label && !isMaxed(btn)) label.textContent = 'Add';
          }, 1200);
        }
      });
    });

    return false;
  };

  /* ------------------------------------------------------------
     STEPPER (2026-10-20, founder: "after added to cart it shows again the same button
     how does the customer will no if he added already ... show max when all the
     quatities we have added"). Blinkit/Zepto pattern: once a product is in the cart,
     its card's ADD becomes [ - n + ] showing the cart quantity; - at 1 removes it
     (back to ADD); + greys out with a MAX tag when every unit in stock is in the cart.
     Covers grid cards (.ss-card-add) and home cards (.spc-add). The fee line is
     re-checked by the existing ss-cart:fetched enforcement after every change.
  ------------------------------------------------------------ */
  var STEP_SEL = '.ss-card-add[data-variant-id], .spc-add[data-vid]';
  function stepVid(btn) { return btn.getAttribute('data-variant-id') || btn.getAttribute('data-vid'); }
  function stepStock(btn) {   /* units we may sell, or Infinity when untracked / oversell allowed */
    var m = btn.getAttribute('data-inventory-management') || btn.getAttribute('data-vm');
    var p = btn.getAttribute('data-inventory-policy') || btn.getAttribute('data-vpolicy');
    var q = parseInt(btn.getAttribute('data-inventory-quantity') || btn.getAttribute('data-vi') || '0', 10);
    return (m === 'shopify' && p !== 'continue' && q > 0) ? q : Infinity;
  }
  function syncSteppers() {
    document.querySelectorAll(STEP_SEL).forEach(function (btn) {
      var vid = stepVid(btn), n = getCartQty(vid), step = btn.nextElementSibling;
      if (!step || !step.classList.contains('ss-step')) {
        if (!n) return;
        step = document.createElement('div');
        step.className = 'ss-step';
        step.innerHTML = '<button type="button" class="ss-step__b" data-d="-1" aria-label="Remove one">&minus;</button>' +
          '<span class="ss-step__n" aria-live="polite"></span>' +
          '<button type="button" class="ss-step__b" data-d="1" aria-label="Add one more">+</button>' +
          '<span class="ss-step__max">Max</span>';
        btn.parentNode.insertBefore(step, btn.nextSibling);
      }
      if (step.classList.contains('is-busy')) return;
      var on = n > 0, max = n >= stepStock(btn);
      btn.hidden = on;
      btn.style.display = on ? 'none' : '';
      step.hidden = !on;
      step.querySelector('.ss-step__n').textContent = n;
      step.querySelector('[data-d="1"]').disabled = max;
      step.classList.toggle('is-max', max);
    });
  }
  document.addEventListener('click', function (e) {
    var b = e.target.closest('.ss-step__b');
    if (!b || b.closest('.ss-step--sticky')) return;   /* product-page sticky bar runs its own stepper */
    e.preventDefault(); e.stopPropagation();
    var step = b.parentNode, btn = step.previousElementSibling, vid = stepVid(btn), d = +b.getAttribute('data-d');
    if (step.classList.contains('is-busy') || (d > 0 && b.disabled)) return;
    step.classList.add('is-busy');
    var done = function () { step.classList.remove('is-busy'); syncSteppers(); };
    fetchCart().then(function () {
      var n = getCartQty(vid);
      if (d > 0 && n >= stepStock(btn)) return done();
      step.querySelector('.ss-step__n').textContent = Math.max(0, n + d);   /* instant feedback */
      var req = d > 0
        ? add(vid, 1, { openDrawer: false })
        : fetch('/cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'same-origin',
            body: JSON.stringify({ id: String(vid), quantity: Math.max(0, n - 1) }) })
            .then(function (r) { if (!r.ok) throw new Error('change ' + r.status); return fetchCart(); })
            .then(function (cart) {
              document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'ss-step', cart: cart, variantId: vid } }));
              signalCrossTab();
            });
      return req.then(done, function () { fetchCart().then(done); });
    });
  });
  /* home cards add through their own code: re-read the cart when anyone else changes it */
  document.addEventListener('cart:updated', function (e) {
    var src = e.detail && e.detail.source;
    if (src !== 'ss-cart-add' && src !== 'ss-step') fetchCart();
  });

  window.SlickCart = {
    fetch:           fetchCart,
    add:             add,
    realItemCount:   realItemCount,
    getQty:          getCartQty,
    isMaxed:         isMaxed,
    hardMax:         hardMax,
    syncGridButtons: syncGridButtons,
    isReady:         function () { return ready; }
  };
 
  /* ------------------------------------------------------------
     Boot
  ------------------------------------------------------------ */
  function boot() {
    fetchCart();
 
    // Cross-tab sync
    window.addEventListener('storage', function (e) {
      if (e.key === STORAGE_TS_KEY) {
        fetchCart();
      }
    });
 
    // Listen for cart changes from OTHER sources (e.g. cart drawer
    // quantity edits via Dawn's own cart-items component).
    document.addEventListener('cart:updated', function (e) {
      var source = e && e.detail && e.detail.source;
      if (source === 'ss-cart-add') return;  // we already refreshed
      fetchCart();
    });
 
    // Re-sync buttons when new ones appear (e.g. related products
    // load after page render).
    var debounceTimer = null;
    var observer = new MutationObserver(function () {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(syncGridButtons, 200);
    });
    observer.observe(document.documentElement, {
      childList: true,
      subtree:   true
    });
  }
 
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();


/* ============================================================
   SLICK CART DRAWER MODULE (2026-07-16)
   Upsell row + terms gate + shipbar fill animation.

   Lives HERE (persistent, deferred) on purpose: the drawer's
   innerHTML is replaced by section rendering on every cart
   change, so inline <script> inside the drawer would never
   re-run. This module re-hydrates after each re-render via a
   debounced MutationObserver on the drawer.
   ============================================================ */
(function () {
  'use strict';

  var UPSELL_LIMIT = 6; /* horizontal scroll row fits more than the old vertical list */
  var esc = function (s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  };
  var money = function (cents) {
    var n = Math.round(cents) / 100;
    var s = (n % 1 === 0 ? String(n) : n.toFixed(2)).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    /* format comes from shop.money_format via the upsell zone's data attr,
       so the drawer always matches the store-level currency setting */
    var zone = document.querySelector('[data-ss-cart-upsell]');
    var fmt = zone && zone.dataset.moneyFormat;
    return fmt && fmt.indexOf('{{') > -1 ? fmt.replace(/\{\{\s*amount[^}]*\}\}/, s).replace('Rs. ', '₹') : '₹' + s;   /* store format still 'Rs.' (13-Oct) */
  };

  /* ---------- terms gate ---------- */
  var TERMS_KEY = 'ss_cart_terms_ok';

  document.addEventListener('click', function (e) {
    /* 2026-07-18: same gate also guards the cart PAGE checkout button
       (data-ss-checkout-gate on the sticky bar CTA). */
    var checkout = e.target.closest('#CartDrawer-Checkout, [data-ss-checkout-gate]');
    if (checkout) {
      var box = document.querySelector('[data-ss-cart-terms]');
      if (box && !box.checked) {
        e.preventDefault();
        e.stopPropagation();
        var label = box.closest('.ss-cart-terms');
        if (label) {
          label.classList.remove('ss-cart-terms--error');
          void label.offsetWidth; /* restart shake animation */
          label.classList.add('ss-cart-terms--error');
        }
        box.focus();
      }
      return;
    }
    var terms = e.target.closest('[data-ss-cart-terms]');
    if (terms) {
      try { sessionStorage.setItem(TERMS_KEY, terms.checked ? '1' : '0'); } catch (err) {}
      var lbl = terms.closest('.ss-cart-terms');
      if (lbl && terms.checked) lbl.classList.remove('ss-cart-terms--error');
    }
  }, true);

  function restoreTerms() {
    var box = document.querySelector('[data-ss-cart-terms]');
    if (!box) return;
    /* Server renders the default (pre-checked when the setting is on). An
       explicit choice made this session — either way — wins over the default. */
    try {
      var saved = sessionStorage.getItem(TERMS_KEY);
      if (saved === '1') box.checked = true;
      if (saved === '0') box.checked = false;
    } catch (err) {}
  }

  /* ---------- upsell row ---------- */
  var upsellCache = null;   // {productId, items}
  var upsellLoading = false;

  function renderUpsell(zone, products) {
    if (!products.length) { zone.hidden = true; return; }
    var heading = zone.dataset.heading || 'You may also like';
    var html = '<p class="ss-cart-upsell__heading">' + heading + '</p><div class="ss-cart-upsell__row">';
    products.forEach(function (p) {
      var v = p.variants && p.variants[0];
      if (!v) return;
      var img = p.featured_image ? p.featured_image.replace(/(\.[a-z]+)(\?|$)/, '_360x$1$2') : '';
      html += '<div class="ss-cart-upsell__card">'
        + (img ? '<a href="' + esc(p.url) + '"><img src="' + esc(img) + '" alt="" loading="lazy" width="360" height="360"></a>' : '')
        + '<div class="ss-cart-upsell__info">'
        + '<a class="ss-cart-upsell__title" href="' + esc(p.url) + '">' + esc(p.title) + '</a>'
        + '<span class="ss-cart-upsell__price">' + money(v.price)
        + (v.compare_at_price && v.compare_at_price > v.price ? ' <s>' + money(v.compare_at_price) + '</s>' : '')
        + '</span></div>'
        + (v.available
          ? '<button type="button" class="ss-cart-upsell__add" data-ss-upsell-add data-variant-id="' + v.id + '">Add</button>'
          : '')
        + '</div>';
    });
    html += '</div>';
    zone.innerHTML = html;
    zone.hidden = false;
  }

  /* "Complete the set" (founder 13-Oct): the drawer shows the SAME rule-based list as the cart page,
     read from sections/ss-cart-recommendations.liquid (JSON block). Falls back to related products. */
  var setSig = '';
  function refreshUpsell() {
    var zone = document.querySelector('[data-ss-cart-upsell]');
    if (!zone) return;
    fetch('/cart.js', { cache: 'no-store' }).then(function (r) { return r.json(); }).then(function (cart) {
      var sig = (cart.items || []).map(function (i) { return i.product_id + 'x' + i.quantity; }).sort().join(',');
      if (sig === setSig && zone.dataset.setRendered === '1') return;
      setSig = sig;
      return fetch('/?section_id=ss-cart-recommendations', { cache: 'no-store' }).then(function (r) { return r.text(); }).then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var j = doc.querySelector('[data-ss-set-json]');
        var data = j ? JSON.parse(j.textContent) : null;
        if (data && data.products && data.products.length) {
          zone.dataset.heading = data.title;
          zone.dataset.setRendered = '1';
          renderUpsell(zone, data.products.slice(0, UPSELL_LIMIT));
        } else {
          zone.dataset.setRendered = '';
          refreshRelatedUpsell();
        }
      });
    }).catch(function () { refreshRelatedUpsell(); });
  }

  function refreshRelatedUpsell() {
    var zone = document.querySelector('[data-ss-cart-upsell]');
    if (!zone) return;
    var pid = zone.dataset.firstProductId;
    if (!pid) return;
    // Loop guard: our own render mutates the drawer, which re-fires the
    // observer → without this the module would re-render every 150ms forever.
    if (zone.dataset.renderedFor === pid) return;
    zone.dataset.renderedFor = pid;

    var apply = function (items) {
      // exclude products already in the cart
      fetch('/cart.js').then(function (r) { return r.json(); }).then(function (cart) {
        var inCart = {};
        (cart.items || []).forEach(function (i) { inCart[i.product_id] = true; });
        /* never recommend products already in cart, nor the gift-wrap or
           marketplace-fee service products (2026-07-18) */
        var feeHandle = (window.SS_CART_CONFIG || {}).feeHandle || 'marketplace-fee';
        renderUpsell(zone, items.filter(function (p) { return !inCart[p.id] && p.handle !== 'gift-wrap' && p.handle !== feeHandle; }).slice(0, UPSELL_LIMIT));
      }).catch(function () { renderUpsell(zone, items.slice(0, UPSELL_LIMIT)); });
    };

    if (upsellCache && upsellCache.productId === pid) { apply(upsellCache.items); return; }
    if (upsellLoading) return;
    upsellLoading = true;
    fetch('/recommendations/products.json?product_id=' + pid + '&limit=' + (UPSELL_LIMIT * 2) + '&intent=related')
      .then(function (r) { return r.json(); })
      .then(function (data) {
        upsellCache = { productId: pid, items: data.products || [] };
        apply(upsellCache.items);
      })
      .catch(function () {})
      .finally(function () { upsellLoading = false; });
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-ss-upsell-add]');
    if (!btn) return;
    e.preventDefault();
    btn.disabled = true;
    btn.textContent = '...';
    var done = function () { btn.textContent = 'Added'; };
    if (window.SlickCart && typeof window.SlickCart.add === 'function') {
      window.SlickCart.add(btn.dataset.variantId, 1, {}).then(done).catch(function () {
        btn.disabled = false; btn.textContent = 'Add';
      });
    } else {
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: Number(btn.dataset.variantId), quantity: 1 })
      }).then(function () {
        done();
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'ss-upsell' } }));
      }).catch(function () { btn.disabled = false; btn.textContent = 'Add'; });
    }
  }, true);

  /* ---------- gift wrap toggle (2026-07-17) ----------
     Checkbox state is server-rendered from cart contents (see
     cart-drawer.liquid), so this handler only has to mutate the cart and
     re-render; the fresh markup carries the new truth. */
  document.addEventListener('change', function (e) {
    var box = e.target.closest('[data-ss-cart-gift]');
    if (!box) return;
    /* ROUND 3 FIX (2026-07-18): this checkbox now lives INSIDE <cart-items>
       on the cart page, and Dawn's CartItems binds a bubble-phase 'change'
       listener that calls updateQuantity(event.target.dataset.index, …) —
       the checkbox has no index, so Dawn crashed in resetQuantityInput
       (null getAttribute — the "2 stray errors" of round 2 phase A, finally
       root-caused). We own this control: stop propagation entirely. */
    e.stopPropagation();
    var vid = box.dataset.variantId;
    box.disabled = true;
    var cartEl = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    var revert = function () { box.checked = !box.checked; box.disabled = false; };
    if (box.checked) {
      if (window.SlickCart && typeof window.SlickCart.add === 'function') {
        window.SlickCart.add(vid, 1, { onError: revert });
      } else revert();
    } else {
      var sections = [];
      try { sections = cartEl.getSectionsToRender().map(function (s) { return s.id; }); } catch (err) {}
      var fd = new FormData();
      fd.append('id', vid);
      fd.append('quantity', 0);
      if (sections.length) {
        fd.append('sections', sections.join(','));
        fd.append('sections_url', window.location.pathname);
      }
      fetch('/cart/change.js', { method: 'POST', body: fd, credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .then(function (res) {
          if (cartEl && typeof cartEl.renderContents === 'function') {
            try { cartEl.renderContents(res); } catch (err) {}
          }
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'ss-giftwrap' } }));
        })
        .catch(revert);
    }
  }, true);

  /* ---------- shipbar entrance animation ---------- */
  function animateShipbar() {
    var fill = document.querySelector('[data-ss-shipbar] .ss-shipbar__fill');
    if (!fill || fill.dataset.animated) return;
    fill.dataset.animated = '1';
    var target = fill.style.width;
    fill.style.width = '0%';
    requestAnimationFrame(function () {
      requestAnimationFrame(function () { fill.style.width = target; });
    });
  }

  /* ---------- re-hydrate after each drawer render ---------- */
  function syncEmptyClass() {
    // DAWN BUG (this Dawn version): component-cart-drawer.css hides the header
    // and items via `cart-drawer.is-empty ...`, but renderContents() only
    // removes is-empty from .drawer__inner — so a drawer that loaded with an
    // empty cart stays "empty-looking" (blank header/items) after an add.
    // Keep the HOST element's class in sync with actual cart contents.
    var cd = document.querySelector('cart-drawer');
    if (!cd) return;
    var hasItems = !!cd.querySelector('.cart-item');
    cd.classList.toggle('is-empty', !hasItems);
    var inner = cd.querySelector('.drawer__inner');
    if (inner) inner.classList.toggle('is-empty', !hasItems);
  }

  function hydrate() {
    syncEmptyClass();
    restoreTerms();
    refreshUpsell();
    animateShipbar();
  }

  function boot() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    hydrate();
    var t = null;
    new MutationObserver(function () {
      clearTimeout(t);
      t = setTimeout(hydrate, 150);
    }).observe(drawer, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();


/* ============================================================
   SLICK CART — BLINKIT CART PAGE MODULE (2026-07-18)
   Runs only when settings.cart_type == 'page' (config bridge:
   window.SS_CART_CONFIG, set inline in layout/theme.liquid).

   Responsibilities:
   1. MARKETPLACE FEE enforcement (server-derived, every page):
      - >=1 real item + no fee line  -> add fee qty 1 (silent)
      - fee qty != 1                 -> set to 1
      - cart contains ONLY the fee   -> remove it
      Fee never renders in item lists; only in Bill details.
   2. "View cart" pill after silent add-to-cart (toggleable).
   3. Cart PAGE behaviors: section refresh after our own cart
      mutations, minus-at-1 removes the line, move-to-wishlist,
      Blinkit-style recommendations grid, terms restore.
   ============================================================ */
(function () {
  'use strict';

  var CFG = window.SS_CART_CONFIG || {};
  if (CFG.cartType !== 'page') return;

  var CART_URL = CFG.cartUrl || '/cart';
  var TERMS_KEY = 'ss_cart_terms_ok';

  function onCartPage() {
    var p = window.location.pathname.replace(/\/+$/, '');
    return p === CART_URL.replace(/\/+$/, '');
  }

  var esc = function (s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  };

  /* money(): back in round 3 — the coupon applied-state pill shows the real
     discount amount from cart JSON (cents), formatted per shop.money_format. */
  function money(cents) {
    var n = Math.round(cents) / 100;
    var s = (n % 1 === 0 ? String(n) : n.toFixed(2)).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var fmt = CFG.moneyFormat;
    return fmt && fmt.indexOf('{{') > -1 ? fmt.replace(/\{\{\s*amount[^}]*\}\}/, s).replace('Rs. ', '₹') : '₹' + s;   /* store format still 'Rs.' (13-Oct) */
  }

  /* ------------------------------------------------------------
     1. MARKETPLACE FEE ENFORCEMENT
     Reacts to every fresh cart state (ss-cart:fetched from
     SlickCart.fetch). Self-terminating: each mutation triggers a
     re-fetch whose state then satisfies the rules (no-op).
  ------------------------------------------------------------ */
  var feeBusy = false;
  var feeFailures = 0;

  /* ROUND 5 (2026-07-18, founder live bug): gift wrap could stack past qty 1
     (two overlapping PDP addIfWanted calls both read "absent" before either
     add landed — check-before-add is not atomic). Single-source fix, same
     pattern as the fee: EVERY reconciliation pass also clamps the gift-wrap
     line to qty 1. One mutation per pass (fee first, gift next pass) — each
     mutation triggers a re-fetch whose state drives the next decision, so
     there are no races and the shared 3-failure circuit breaker still holds.
     Toggle OFF still removes the line; badge/shipbar/bill math unchanged. */
  var GIFT_HANDLE = 'gift-wrap';

  function enforceFee(cart) {
    if (!cart || !Array.isArray(cart.items)) return;
    if (feeBusy || feeFailures >= 3) return;

    var feeQty = 0;
    var feeKey = null;
    var realLines = 0;
    var giftLines = [];
    var giftQty = 0;
    cart.items.forEach(function (item) {
      if (item.handle === CFG.feeHandle) {
        feeQty += item.quantity;
        feeKey = feeKey || item.key;
      } else {
        if (item.handle === GIFT_HANDLE) { giftLines.push(item); giftQty += item.quantity; }
        realLines += 1;
      }
    });

    var req = null;
    var feeConfigured = CFG.feeEnabled && CFG.feeVariantId && CFG.feeHandle;
    if (!CFG.feeEnabled && feeKey) {
      /* 2026-10-20 founder: "i want to remove marketplace fee" — fee switched off in theme
         settings; carts that already hold the fee line lose it on their next cart read */
      req = fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ id: String(feeKey), quantity: 0 })
      });
    } else if (feeConfigured && realLines > 0 && feeQty === 0) {
      req = fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        credentials: 'same-origin',
        body: JSON.stringify({ id: Number(CFG.feeVariantId), quantity: 1, properties: { _fee: '1' } })
      });
    } else if (feeConfigured && realLines > 0 && feeQty !== 1) {
      req = fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ id: String(CFG.feeVariantId), quantity: 1 })
      });
    } else if (feeConfigured && realLines === 0 && feeQty > 0) {
      req = fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ id: String(CFG.feeVariantId), quantity: 0 })
      });
    } else if (giftQty > 1) {
      /* gift-wrap clamp: one unit TOTAL. Addressed by line key (immune to
         variant-id type mismatches). If the excess sits on one line, clamp
         it to 1; if it is a duplicate line (same variant, different props),
         remove that extra line. One mutation per pass; the follow-up fetch
         re-evaluates until total == 1. */
      var target = null;
      for (var gi = 0; gi < giftLines.length; gi++) {
        if (giftLines[gi].quantity > 1) { target = { key: giftLines[gi].key, qty: 1 }; break; }
      }
      if (!target && giftLines.length > 1) target = { key: giftLines[1].key, qty: 0 };
      if (target) {
        req = fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ id: String(target.key), quantity: target.qty })
        });
      }
    }
    if (!req) return;

    feeBusy = true;
    req
      .then(function (r) {
        if (!r.ok) throw new Error('fee enforcement HTTP ' + r.status);
        return r.json();
      })
      .then(function () {
        feeFailures = 0;
        feeBusy = false;
        if (window.SlickCart) window.SlickCart.fetch();
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'ss-fee' } }));
      })
      .catch(function (err) {
        feeBusy = false;
        feeFailures += 1;
        console.warn('SlickCart fee enforcement failed:', err);
      });
  }

  document.addEventListener('ss-cart:fetched', function (e) {
    enforceFee(e.detail && e.detail.cart);
  });

  /* ------------------------------------------------------------
     2. "VIEW CART" PILL (silent add feedback, Blinkit-style)
     Shown after a successful add anywhere except the cart page.
  ------------------------------------------------------------ */
  var pillTimer = null;

  function showPill() {
    if (CFG.pillEnabled === false || onCartPage()) return;
    var pill = document.getElementById('ss-viewcart-pill');
    if (!pill) {
      pill = document.createElement('div');
      pill.id = 'ss-viewcart-pill';
      pill.className = 'ss-viewcart-pill';
      pill.innerHTML =
        '<a class="ss-viewcart-pill__link" href="' + esc(CART_URL) + '">' +
        '<span class="ss-viewcart-pill__count" data-ss-pill-count></span>' +
        '<span class="ss-viewcart-pill__label">' + esc(CFG.pillLabel || 'View cart') + '</span>' +
        '<span class="ss-viewcart-pill__arrow" aria-hidden="true">&rsaquo;</span></a>' +
        '<button type="button" class="ss-viewcart-pill__close" aria-label="Dismiss">&times;</button>';
      document.body.appendChild(pill);
      pill.querySelector('.ss-viewcart-pill__close').addEventListener('click', function () {
        pill.classList.remove('is-visible');
        clearTimeout(pillTimer);
      });
    }
    /* count = real items only (badge logic already excludes the fee) */
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        /* round 4: single-source count rule (fee excluded, gift counts) */
        var count = window.SlickCart ? window.SlickCart.realItemCount(cart) : (cart.item_count || 0);
        var el = pill.querySelector('[data-ss-pill-count]');
        if (el) el.textContent = count > 0 ? count : '';
        /* 2026-10-20 (founder: "view cart button ... get hider under that sticky bar"): sit just above
           the product page's sticky Add-to-cart bar when it is showing */
        var bar = document.querySelector('.ss-sticky.ss-sticky--on');
        pill.style.bottom = bar ? (window.innerHeight - bar.getBoundingClientRect().top + 12) + 'px' : '';
        pill.classList.add('is-visible');
        clearTimeout(pillTimer);
        pillTimer = setTimeout(function () { pill.classList.remove('is-visible'); }, 4000);
      })
      .catch(function () {});
  }

  document.addEventListener('cart:updated', function (e) {
    var d = e.detail || {};
    if (d.source !== 'ss-cart-add') return;
    if (String(d.variantId) === String(CFG.feeVariantId)) return;
    showPill();
  });

  /* ------------------------------------------------------------
     3. CART PAGE BEHAVIORS
  ------------------------------------------------------------ */
  if (!onCartPage()) return;

  /* ---- section refresh after OUR OWN cart mutations (gift wrap,
     fee, upsell add, move-to-wishlist). Dawn's cart.js already
     re-renders after qty-stepper changes (source 'cart-items'). ---- */
  var refreshTimer = null;
  var refreshing = false;

  function refreshSections() {
    if (refreshing) return;
    var itemsHost = document.getElementById('main-cart-items');
    var footHost = document.getElementById('main-cart-footer');
    if (!itemsHost || !footHost) { window.location.reload(); return; }
    var ids = [itemsHost.dataset.id, footHost.dataset.id];
    refreshing = true;
    Promise.all([
      fetch(CART_URL + '?sections=' + ids.join(','), { credentials: 'same-origin' }).then(function (r) { return r.json(); }),
      fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); })
    ]).then(function (out) {
      var sections = out[0];
      var cart = out[1];
      [itemsHost, footHost].forEach(function (host, i) {
        var html = sections[ids[i]];
        if (!html) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.querySelector('.js-contents');
        var target = host.querySelector('.js-contents');
        if (fresh && target) target.innerHTML = fresh.innerHTML;
      });
      var isEmpty = !cart || cart.item_count === 0;
      var cartItemsEl = document.querySelector('cart-items');
      if (cartItemsEl) cartItemsEl.classList.toggle('is-empty', isEmpty);
      footHost.classList.toggle('is-empty', isEmpty);
      restorePageTerms();
      renderPageUpsell();
      hydrateCoupon(cart); /* round 3: swapped .js-contents recreated the state div */
    }).catch(function () {
      window.location.reload();
    }).finally(function () {
      refreshing = false;
    });
  }

  function queueRefresh() {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(refreshSections, 150);
  }

  document.addEventListener('cart:updated', function (e) {
    var source = (e.detail || {}).source || '';
    /* 'cart-items' = Dawn already swapped the sections itself */
    if (source === 'cart-items') return;
    queueRefresh();
  });

  /* Dawn qty-stepper re-renders replace .js-contents — re-run page
     hydration (terms restore), keep the upsell exclusions fresh, and
     re-fetch the cart so fee enforcement reacts to Dawn-driven changes
     (Dawn's cart.js publishes on its own pub/sub, not on cart:updated —
     without this, removing the last real item would strand the fee). */
  function hookDawnRerenders() {
    var host = document.getElementById('main-cart-items');
    if (!host) return;
    if (host.dataset.ssObserved) return; /* editor re-loads call this again on a FRESH host */
    host.dataset.ssObserved = '1';
    var t = null;
    new MutationObserver(function () {
      clearTimeout(t);
      t = setTimeout(function () {
        restorePageTerms();
        renderPageUpsell();
        if (window.SlickCart) window.SlickCart.fetch();
      }, 200);
    }).observe(host, { childList: true, subtree: true });
  }

  /* ---- terms restore (same sessionStorage contract as drawer) ---- */
  function restorePageTerms() {
    var box = document.querySelector('[data-ss-cart-terms]');
    if (!box) return;
    try {
      var saved = sessionStorage.getItem(TERMS_KEY);
      if (saved === '1') box.checked = true;
      if (saved === '0') box.checked = false;
    } catch (err) {}
  }

  /* ---- minus at qty 1 removes the line (Blinkit behavior) ---- */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('.ss-ci .ss-qty button[name="minus"]');
    if (!btn) return;
    var row = btn.closest('.ss-ci');
    var input = row && row.querySelector('.ss-qty__input');
    if (!input || parseInt(input.value, 10) > 1) return;
    e.preventDefault();
    e.stopPropagation();
    var ci = document.querySelector('cart-items');
    if (ci && typeof ci.updateQuantity === 'function') {
      ci.updateQuantity(input.dataset.index, 0, e);
    }
  }, true);

  /* ---- move to wishlist: save via the shared SlickWishlist store
     (same localStorage the hearts use), then remove the line ---- */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-ss-move-wishlist]');
    if (!btn || btn.disabled) return;
    e.preventDefault();
    btn.disabled = true;

    /* Round 2 (2026-07-18): single-source idempotent add — the shared
       engine's SlickWishlist.add() (ss-wishlist.js) owns the VARIANT-level
       check-before-add and fires the same events the hearts fire, so the
       wishlist page + header badge reflect the move identically. The line
       item's exact variant id (data-variant-id = item.variant_id) is the
       stored identity. */
    try {
      if (window.SlickWishlist && typeof window.SlickWishlist.add === 'function') {
        window.SlickWishlist.add({
          id: btn.dataset.productId,
          handle: btn.dataset.productHandle || '',
          variantId: btn.dataset.variantId,
          variantTitle: btn.dataset.variantTitle || '',
          title: btn.dataset.productTitle || 'Product',
          image: btn.dataset.productImage || '',
          url: btn.dataset.productUrl || '#',
          price: btn.dataset.productPrice || '',
          priceRaw: btn.dataset.productPriceRaw || '',
          comparePrice: btn.dataset.productComparePrice || '',
          comparePriceRaw: btn.dataset.productComparePriceRaw || '',
          vendor: btn.dataset.productVendor || '',
          available: btn.dataset.productAvailable !== 'false'
        });
      }
    } catch (err) {
      console.warn('Move to wishlist: save failed (line still removed):', err);
    }

    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ id: btn.dataset.lineKey, quantity: 0 })
    })
      .then(function (r) { return r.json(); })
      .then(function () {
        if (window.SlickCart) window.SlickCart.fetch();
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'ss-wishlist-move' } }));
      })
      .catch(function () { btn.disabled = false; });
  }, true);

  /* ---- COUPON card (Round 3, Zomato-style) ----
     Native mechanism (shopify.dev Cart Ajax API, verified live on this store
     18-Jul with FIRSTCUT): POST /cart/update.js {discount:'CODE'} applies,
     {discount:''} removes; /cart.js returns discount_codes[{code,applicable}]
     plus REAL discounted totals (total_discount, cart_level_discount_
     applications, per-line discounts) — so the bill re-render shows genuine
     math and the CTA number is exactly what checkout charges. One code at a
     time (apply replaces). HONESTY: when a code is on the cart but not
     applicable (min not met / invalid), we show the code + the neutral
     settings text and NO amount — nothing fabricated. */
  var couponBusy = false;

  function couponSettings(zone) {
    return {
      removeLabel: zone.dataset.removeLabel || 'Remove',
      appliedLabel: zone.dataset.appliedLabel || 'applied',
      naText: zone.dataset.naText || 'Not applicable to this cart'
    };
  }

  function hydrateCoupon(cart) {
    var zone = document.querySelector('[data-ss-coupon]');
    if (!zone || !cart) return;
    var state = zone.querySelector('[data-ss-coupon-state]');
    if (!state) return;
    var cfg = couponSettings(zone);
    var codes = (cart.discount_codes || []).filter(function (c) { return c && c.code; });
    /* (Founder decision 18-Jul: no featured-coupon list in the cart —
       the card is input + Apply + state only.) */

    if (!codes.length) {
      state.hidden = true;
      state.innerHTML = '';
      return;
    }
    /* total_discount (cents) covers cart-level + line-level allocations —
       with a single code on the cart it IS that code's real amount. */
    var amt = cart.total_discount || 0;
    var html = codes.map(function (c) {
      var name = '‘' + esc(c.code) + '’';
      if (c.applicable && amt > 0) {
        return '<div class="ss-coupon__pill ss-coupon__pill--ok">'
          + '<span class="ss-coupon__tick" aria-hidden="true">✓</span>'
          + '<span>' + name + ' ' + esc(cfg.appliedLabel) + ' — ' + money(amt) + '</span>'
          + '<button type="button" class="ss-coupon__remove" data-ss-coupon-remove>' + esc(cfg.removeLabel) + '</button></div>';
      }
      return '<div class="ss-coupon__pill ss-coupon__pill--na">'
        + '<span>' + name + ' — ' + esc(cfg.naText) + '</span>'
        + '<button type="button" class="ss-coupon__remove" data-ss-coupon-remove>' + esc(cfg.removeLabel) + '</button></div>';
    }).join('');
    state.innerHTML = html;
    state.hidden = false;
  }

  function couponMutate(code) {
    if (couponBusy) return;
    couponBusy = true;
    var zone = document.querySelector('[data-ss-coupon]');
    if (zone) zone.classList.add('is-busy');
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'same-origin',
      body: JSON.stringify({ discount: code })
    })
      .then(function (r) { return r.json(); })
      .then(function () {
        couponBusy = false;
        if (zone) zone.classList.remove('is-busy');
        var input = zone && zone.querySelector('[data-ss-coupon-input]');
        if (input && code) input.value = '';
        if (window.SlickCart) window.SlickCart.fetch(); /* fires ss-cart:fetched -> hydrateCoupon */
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'ss-coupon' } }));
      })
      .catch(function () {
        couponBusy = false;
        if (zone) zone.classList.remove('is-busy');
      });
  }

  document.addEventListener('click', function (e) {
    var applyBtn = e.target.closest('[data-ss-coupon-apply]');
    if (applyBtn) {
      var zone = applyBtn.closest('[data-ss-coupon]');
      var input = zone && zone.querySelector('[data-ss-coupon-input]');
      var code = input && input.value.trim();
      if (code) couponMutate(code);
      return;
    }
    if (e.target.closest('[data-ss-coupon-remove]')) couponMutate('');
  }, true);

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter') return;
    var input = e.target.closest('[data-ss-coupon-input]');
    if (!input) return;
    e.preventDefault(); /* never submit form#cart */
    var code = input.value.trim();
    if (code) couponMutate(code);
  }, true);

  /* The coupon input sits inside <cart-items>; its blur 'change' event must
     never reach Dawn's CartItems.onChange (no dataset.index -> null crash,
     same failure mode as the gift toggle — see module 2). */
  document.addEventListener('change', function (e) {
    if (e.target.closest('[data-ss-coupon-input]')) e.stopPropagation();
  }, true);

  document.addEventListener('ss-cart:fetched', function (e) {
    hydrateCoupon(e.detail && e.detail.cart);
  });

  /* ---- "You might also like" grid (Blinkit placement) ----
     Round 2 (2026-07-18, founder: card parity): the zone is filled with the
     SERVER-RENDERED sections/ss-cart-recommendations.liquid — the shared
     card-product snippet, fetched through Shopify's recommendations
     endpoint with a section_id (Dawn related-products pattern). Exclusions
     (in-cart products / gift-wrap / marketplace fee) happen inside the
     section, where the session cart is authoritative. We cache per
     cart-signature so observer-driven re-runs don't refetch needlessly. ---- */
  var recoCache = null;   // { sig, html }
  var recoLoading = false;

  function injectPageUpsell(zone, html) {
    zone.innerHTML = html;
    zone.hidden = html === '';
    /* hearts inside the injected cards: paint saved-state immediately
       (the shared engine's MutationObserver would also catch it) */
    if (typeof window.syncSlickWishlistButtons === 'function') {
      window.syncSlickWishlistButtons();
    }
  }

  function renderPageUpsell() {
    var zone = document.querySelector('[data-ss-page-upsell]');
    if (!zone) return;
    var pid = zone.dataset.firstProductId;
    var base = zone.dataset.url;
    if (!pid || !base) { zone.hidden = true; return; }

    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (!cart || cart.item_count === 0) { injectPageUpsell(zone, ''); return; }
        var sig = pid + '|' + (cart.items || []).map(function (i) { return i.product_id + 'x' + i.quantity; }).sort().join(',');
        if (recoCache && recoCache.sig === sig) {
          if (zone.dataset.renderedSig !== sig) {
            zone.dataset.renderedSig = sig;
            injectPageUpsell(zone, recoCache.html);
          }
          return;
        }
        if (recoLoading) return;
        recoLoading = true;
        var url = base + (base.indexOf('?') > -1 ? '&' : '?')
          + 'product_id=' + encodeURIComponent(pid)
          + '&limit=10&intent=related&section_id=ss-cart-recommendations';
        fetch(url, { credentials: 'same-origin' })
          .then(function (r) { return r.text(); })
          .then(function (text) {
            var doc = new DOMParser().parseFromString(text, 'text/html');
            var root = doc.querySelector('[data-ss-cart-reco]');
            var html = root ? root.outerHTML : '';
            recoCache = { sig: sig, html: html };
            zone.dataset.renderedSig = sig;
            injectPageUpsell(zone, html);
          })
          .catch(function () {})
          .finally(function () { recoLoading = false; });
      })
      .catch(function () {});
  }

  /* ---- boot ---- */
  function bootPage() {
    restorePageTerms();
    renderPageUpsell();
    hookDawnRerenders();
  }

  /* Theme-editor support (18-Jul round 2): the editor re-renders sections
     via shopify:section:load, replacing the section DOM (upsell zone,
     items host, terms box). Re-hydrate the fresh DOM — founder tests in
     the editor's mobile preview, so this path must work identically. */
  document.addEventListener('shopify:section:load', function () {
    restorePageTerms();
    renderPageUpsell();
    hookDawnRerenders();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootPage);
  } else {
    bootPage();
  }
})();

/* cart page Check out bar = like the product page's sticky Add to cart (founder 19-Oct: "this check out should work like the sticky just like
   hit the bottom it should hide like add to cart in the product page"): pinned to the bottom while you browse, slides away (ss-genz.css
   .ss-ctabar.is-off) when the footer comes into view, back when you scroll up.
   History: 13-Oct the same slide-away hid the ONLY checkout button on big monitors, because a short cart left the footer on screen from the
   start. Fix kept from 19-Oct: the cart content is at least one window tall, so the footer is never on screen when the page opens. */
(function () {
  var foot = document.querySelector('.shopify-section-group-footer-group');
  if (!foot || !document.querySelector('.ss-ctabar')) return;
  var footOn = false;
  function fit() {
    var main = document.getElementById('MainContent'); if (!main) return;
    main.style.minHeight = ''; var top = main.getBoundingClientRect().top + window.scrollY;
    main.style.minHeight = Math.max(0, Math.ceil(window.innerHeight - top)) + 'px';
  }
  function sync() {  /* cart updates re-render the bar, so look it up every time */
    var bar = document.querySelector('.ss-ctabar'); if (!bar) return;
    bar.style.bottom = ''; bar.classList.toggle('is-off', footOn);
  }
  if ('IntersectionObserver' in window) new IntersectionObserver(function (e) { footOn = e[0].intersectionRect.height > 2; sync(); }, { threshold: [0, 0.01, 0.05, 0.2, 1] }).observe(foot);   /* a footer that only TOUCHES the window edge (big screens) is not 'on screen' */
  window.addEventListener('resize', fit);
  new MutationObserver(function () { fit(); sync(); }).observe(document.getElementById('main-cart-footer') || document.body, { childList: true, subtree: true });
  fit(); sync();
})();
